import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, MessageSquare, AlertTriangle, Users, History as HistoryIcon, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Broadcast SMS to every approved/VIP member (with SMS consent).
 * Requires Twilio credentials in backend .env; otherwise runs in MOCK mode
 * (logs each message server-side, marks the campaign as mocked=N).
 */
export default function AdminBroadcast() {
    const nav = useNavigate();
    const [preview, setPreview] = useState(null);
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [confirm, setConfirm] = useState(false);
    const [history, setHistory] = useState([]);
    const [lastResult, setLastResult] = useState(null);

    useEffect(() => {
        if (!localStorage.getItem("sc_admin_token")) {
            nav("/admin/login");
            return;
        }
        refresh();
    }, []);

    const refresh = async () => {
        try {
            const [{ data: p }, { data: h }] = await Promise.all([
                api.get("/admin/broadcast/preview"),
                api.get("/admin/broadcast/history"),
            ]);
            setPreview(p);
            setHistory(h.items || []);
        } catch (e) {
            if (e?.response?.status === 401) nav("/admin/login");
        }
    };

    // ~160 chars per SMS segment (GSM-7). Non-latin drops to 70.
    const chars = message.length;
    const hasNonGsm = /[^\u0000-\u007F\u00a0-\u00ff]/.test(message);
    const segLen = hasNonGsm ? 70 : 160;
    const segments = chars === 0 ? 0 : Math.ceil(chars / segLen);
    const tooLong = chars > 480;
    const tooShort = chars > 0 && chars < 3;

    const send = async () => {
        if (tooLong || tooShort || !message.trim()) return;
        setSending(true);
        try {
            const { data } = await api.post("/admin/broadcast/sms", { message });
            setLastResult(data);
            toast.success(
                data.twilio_live
                    ? `Sent to ${data.sent} members`
                    : `MOCK: logged ${data.mocked} messages (Twilio not connected)`,
                { duration: 6000 }
            );
            setMessage("");
            setConfirm(false);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Broadcast failed");
        } finally {
            setSending(false);
        }
    };

    const sample = () =>
        setMessage(
            "ScottsClub · Members Night i kväll, dörrarna öppnar 22:00. " +
            "Hamngatan 19 · Kungsträdgården. Din QR öppnar dörren."
        );

    return (
        <div className="min-h-[100dvh] pb-24" data-testid="admin-broadcast-page">
            <header className="border-b border-emerald-line/60 bg-bg-secondary/60 backdrop-blur-md">
                <div className="max-w-5xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
                    <Link
                        to="/admin"
                        className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-ink-secondary hover:text-gold"
                        data-testid="back-to-dashboard"
                    >
                        <ArrowLeft size={14} /> Dashboard
                    </Link>
                    <div className="flex items-center gap-2 text-gold text-[11px] tracking-[0.24em] uppercase">
                        <MessageSquare size={14} /> Broadcast SMS
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 lg:px-12 pt-16">
                {/* Head */}
                <div className="label-eyebrow flex items-center gap-3">
                    <span className="w-8 h-px bg-gold" />
                    Bulk SMS to all members
                </div>
                <h1 className="font-serif font-medium text-4xl md:text-5xl mt-4 leading-tight">
                    Message <span className="italic text-gold">everyone</span> at once
                </h1>
                <p className="text-ink-secondary mt-4 max-w-2xl leading-relaxed">
                    Send a single SMS to every approved and VIP member who consented to
                    marketing SMS on their application. Perfect for tonight&apos;s dress
                    code, event invites and last-minute changes.
                </p>

                {/* Stats + Twilio state */}
                <div className="mt-10 grid sm:grid-cols-2 gap-4" data-testid="broadcast-stats">
                    <div className="rounded-sm border border-gold/40 bg-bg-secondary/70 p-5 flex items-center gap-4">
                        <Users size={20} className="text-gold" />
                        <div>
                            <div className="label-eyebrow">Eligible recipients</div>
                            <div className="font-serif text-4xl mt-1 gold-text" data-testid="eligible-count">
                                {preview?.eligible ?? "—"}
                            </div>
                        </div>
                    </div>
                    <div
                        className={`rounded-sm border p-5 flex items-center gap-4 ${
                            preview?.sms_configured
                                ? "border-emerald-500/40 bg-emerald-500/5"
                                : "border-amber-500/40 bg-amber-500/5"
                        }`}
                        data-testid="broadcast-sms-state"
                    >
                        {preview?.sms_configured ? (
                            <Check size={20} className="text-emerald-400" />
                        ) : (
                            <AlertTriangle size={20} className="text-amber-400" />
                        )}
                        <div>
                            <div className="label-eyebrow">Twilio SMS</div>
                            <div className={`font-serif text-2xl mt-1 ${
                                preview?.sms_configured ? "text-emerald-300" : "text-amber-300"
                            }`}>
                                {preview?.sms_configured ? "LIVE" : "MOCK · not connected"}
                            </div>
                        </div>
                    </div>
                </div>

                {!preview?.sms_configured && (
                    <div className="mt-4 text-xs text-amber-300/85 leading-relaxed max-w-2xl">
                        Broadcast will run in MOCK mode: each message is logged server-side
                        for testing, but no real SMS is sent. Add Twilio credentials to{" "}
                        <code className="text-gold font-mono">backend/.env</code> to go live —
                        see the dashboard&apos;s System Status card.
                    </div>
                )}

                {/* Composer */}
                <div className="mt-10">
                    <div className="flex items-center justify-between mb-3">
                        <span className="label-eyebrow">Message</span>
                        <button
                            onClick={sample}
                            type="button"
                            className="text-[10px] tracking-[0.22em] uppercase text-ink-secondary hover:text-gold"
                            data-testid="sample-message"
                        >
                            Insert sample
                        </button>
                    </div>
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Ex: ScottsClub · Members Night i kväll, 22:00. Hamngatan 19. Din QR öppnar dörren."
                        rows={6}
                        maxLength={520}
                        className="w-full bg-bg-secondary/60 border border-emerald-line rounded-sm p-4 text-ink-primary text-[15px] leading-relaxed placeholder:text-ink-muted focus:border-gold outline-none transition-colors resize-y"
                        data-testid="broadcast-message-input"
                    />
                    <div className="mt-2 flex items-center justify-between text-xs" data-testid="broadcast-counter">
                        <div className={`${tooLong ? "text-red-400" : "text-ink-muted"}`}>
                            {chars} / 480 characters · {segments} SMS segment{segments === 1 ? "" : "s"}
                            {hasNonGsm && chars > 0 && " · non-latin (70 chars/seg)"}
                        </div>
                        {tooShort && <div className="text-amber-400">At least 3 characters</div>}
                        {tooLong && <div className="text-red-400">Too long — trim to 480</div>}
                    </div>
                </div>

                {/* Send row */}
                <div className="mt-8 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                    <button
                        onClick={() => setConfirm(true)}
                        disabled={!message.trim() || tooLong || tooShort || sending}
                        data-testid="broadcast-send"
                        className="btn-gold inline-flex items-center justify-center gap-3 px-8 py-3.5 rounded-full font-medium tracking-wide disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <Send size={16} />
                        <span className="text-sm tracking-[0.18em] uppercase">
                            {sending ? "Sending…" : `Send to ${preview?.eligible ?? 0}`}
                        </span>
                    </button>
                    <div className="text-xs text-ink-muted max-w-md leading-relaxed">
                        You can&apos;t undo a broadcast once sent. A short delay between
                        each message keeps Twilio happy.
                    </div>
                </div>

                {/* Last result */}
                {lastResult && (
                    <div
                        className="mt-10 rounded-sm border border-emerald-line/60 bg-bg-secondary/60 p-5"
                        data-testid="last-broadcast-result"
                    >
                        <div className="label-eyebrow flex items-center gap-2">
                            <Check size={12} className="text-emerald-400" /> Last broadcast
                        </div>
                        <div className="mt-3 grid sm:grid-cols-4 gap-4 text-sm">
                            <Metric label="Sent" value={lastResult.sent} accent="emerald" />
                            <Metric label="Mocked" value={lastResult.mocked} accent="amber" />
                            <Metric label="Failed" value={lastResult.failed} accent="red" />
                            <Metric label="Recipients" value={lastResult.recipients_count} />
                        </div>
                        <div className="mt-4 text-ink-secondary italic text-sm border-l border-gold/40 pl-4">
                            &ldquo;{lastResult.message}&rdquo;
                        </div>
                    </div>
                )}

                {/* History */}
                {history.length > 0 && (
                    <div className="mt-16" data-testid="broadcast-history">
                        <div className="label-eyebrow flex items-center gap-2">
                            <HistoryIcon size={12} /> Recent broadcasts
                        </div>
                        <div className="mt-4 divide-y divide-emerald-line/50 border-y border-emerald-line/50">
                            {history.map((h, i) => (
                                <div key={i} className="py-4 grid sm:grid-cols-[130px_1fr_auto] gap-4 items-start">
                                    <div className="text-xs text-ink-muted tracking-wider">
                                        {new Date(h.sent_at).toLocaleString("en-GB", {
                                            day: "2-digit", month: "short",
                                            hour: "2-digit", minute: "2-digit",
                                            timeZone: "Europe/Stockholm",
                                        })}
                                    </div>
                                    <div className="text-sm text-ink-primary leading-snug italic font-serif">
                                        &ldquo;{h.message}&rdquo;
                                    </div>
                                    <div className="flex items-center gap-3 text-[10px] tracking-[0.22em] uppercase">
                                        <span className="text-emerald-300">✓ {h.sent}</span>
                                        {h.mocked > 0 && <span className="text-amber-300">m {h.mocked}</span>}
                                        {h.failed > 0 && <span className="text-red-300">✕ {h.failed}</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </main>

            {/* Confirm modal */}
            {confirm && (
                <div
                    className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
                    data-testid="broadcast-confirm-modal"
                    onClick={() => !sending && setConfirm(false)}
                >
                    <div
                        className="glass-strong border border-gold/30 max-w-md w-full rounded-sm p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="label-eyebrow flex items-center gap-2">
                            <AlertTriangle size={12} className="text-amber-400" />
                            Confirm broadcast
                        </div>
                        <h3 className="font-serif text-2xl mt-3">
                            Send to <span className="gold-text">{preview?.eligible ?? 0} members</span>?
                        </h3>
                        <p className="text-ink-secondary text-sm mt-3">
                            {preview?.sms_configured
                                ? "This will send a real SMS to every eligible member. Cannot be undone."
                                : "Twilio is not connected — this will run in MOCK mode (server logs only)."}
                        </p>
                        <div className="mt-4 text-sm text-ink-primary italic font-serif border-l border-gold/40 pl-4 max-h-40 overflow-auto">
                            &ldquo;{message}&rdquo;
                        </div>
                        <div className="mt-6 flex gap-3 justify-end">
                            <button
                                onClick={() => setConfirm(false)}
                                disabled={sending}
                                className="inline-flex items-center gap-2 px-5 py-2.5 border border-ink-secondary/40 text-ink-secondary hover:text-ink-primary hover:border-ink-primary/40 rounded-full text-[11px] tracking-[0.22em] uppercase disabled:opacity-50"
                                data-testid="broadcast-cancel"
                            >
                                <X size={14} /> Cancel
                            </button>
                            <button
                                onClick={send}
                                disabled={sending}
                                data-testid="broadcast-confirm"
                                className="btn-gold inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-[11px] tracking-[0.22em] uppercase font-medium disabled:opacity-60"
                            >
                                <Send size={14} />
                                {sending ? "Sending…" : "Confirm & Send"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Metric({ label, value, accent }) {
    const colour = {
        emerald: "text-emerald-300",
        amber: "text-amber-300",
        red: "text-red-300",
    }[accent] || "text-ink-primary";
    return (
        <div>
            <div className="label-eyebrow">{label}</div>
            <div className={`font-serif text-3xl mt-1 ${colour}`}>{value ?? 0}</div>
        </div>
    );
}
